// Multi-tab coordination via localStorage-backed tab locks.
//
// Problem: background work (future webhook relay, enrichment queue,
// scheduled-send dispatcher, conversation_metadata sync) shouldn't run
// in every open tab simultaneously. If a user has Slash in three tabs,
// we want exactly one to own each sync responsibility.
//
// Solution: a named lock per work-kind. Each tab has a stable TAB_ID.
// `tryAcquire(key)` attempts to claim the lock; `heartbeat(key)` refreshes
// it periodically; `release(key)` clears it. Locks older than LOCK_TIMEOUT
// are considered dead (tab crashed / closed) and any tab can take over.
//
// Storage: `localStorage` + `BroadcastChannel` for cross-tab visibility.
// `BroadcastChannel` is the message bus that wakes other tabs up when a
// lock changes without them having to poll. Fallback to StorageEvent
// only if BroadcastChannel isn't available.
//
// ADR: docs/adr/0023-tab-lock.md
// Related:
//   - ADR 0022 (Jotai) — `atomTabLock(key)` bridges this into atoms
//   - ADR 0018 (background jobs) — server-side jobs don't need this;
//     this is purely for web-app-side coordination
//   - Kondo's `cursor.ts` + `syncback.ts` use a Supabase-backed
//     variant for *device*-coordinated sync. We're device-local for
//     now; Supabase variant would be a follow-up when we need it.

import { createLogger } from './logger'

const log = createLogger('tab-lock')

// ─── TAB_ID ──────────────────────────────────────────────────────────────────

/**
 * Stable id for this browser tab. Stored in sessionStorage so a tab's id
 * persists across reloads but NOT across closings. A fresh tab gets a
 * fresh id.
 *
 * Generated lazily on first read via `crypto.randomUUID()`. Falls through
 * to a process-local UUID if sessionStorage is unavailable (private mode,
 * CSP) — that means no tab-coordination for that tab, which is acceptable
 * since other tabs also won't see cross-tab writes.
 */
export const TAB_ID: string = (() => {
  const STORAGE_KEY = 'fwdslash:tab-id'
  try {
    if (typeof sessionStorage !== 'undefined') {
      let id = sessionStorage.getItem(STORAGE_KEY)
      if (!id) {
        id = crypto.randomUUID()
        sessionStorage.setItem(STORAGE_KEY, id)
      }
      return id
    }
  } catch {
    // fall through
  }
  return crypto.randomUUID()
})()

// ─── Config ──────────────────────────────────────────────────────────────────

/** How long a lock is considered "live" without a heartbeat. After this,
 * any tab can take over. 60s is a trade-off — long enough that a
 * temporarily-frozen tab (throttled background tab, debugger paused)
 * doesn't lose its lock, short enough that a crashed tab doesn't block
 * forever. */
export const LOCK_TIMEOUT_MS = 60_000

/** Recommended heartbeat cadence for lock holders. 15s = 4 heartbeats
 * per LOCK_TIMEOUT window — survives occasional missed heartbeats. */
export const HEARTBEAT_INTERVAL_MS = 15_000

const STORAGE_PREFIX = 'fwdslash:tab-lock:'
const BROADCAST_CHANNEL_NAME = 'fwdslash:tab-lock'

// ─── State shape on disk ─────────────────────────────────────────────────────

interface LockState {
  /** The tab that holds the lock. */
  tabId: string
  /** First acquisition timestamp. Unchanged across heartbeats. */
  acquiredAt: number
  /** Last heartbeat timestamp. Compared against LOCK_TIMEOUT_MS to
   * decide if the lock is still live. */
  heartbeat: number
}

// ─── Storage helpers ─────────────────────────────────────────────────────────

function storageKey(key: string): string {
  return STORAGE_PREFIX + key
}

function readLock(key: string): LockState | null {
  try {
    const raw = localStorage.getItem(storageKey(key))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Shape check — corrupt entries treated as absent.
    if (
      !parsed ||
      typeof parsed.tabId !== 'string' ||
      typeof parsed.acquiredAt !== 'number' ||
      typeof parsed.heartbeat !== 'number'
    ) return null
    return parsed
  } catch {
    return null
  }
}

function writeLock(key: string, state: LockState): void {
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(state))
    notifyChange(key)
  } catch {
    // storage blocked — write is a no-op; lock won't be visible to other tabs
  }
}

function deleteLock(key: string): void {
  try {
    localStorage.removeItem(storageKey(key))
    notifyChange(key)
  } catch {
    // storage blocked — no-op
  }
}

// ─── BroadcastChannel (cross-tab messaging) ──────────────────────────────────

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (channel) return channel
  try {
    if (typeof BroadcastChannel === 'undefined') return null
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    return channel
  } catch {
    return null
  }
}

function notifyChange(key: string): void {
  const ch = getChannel()
  if (ch) {
    try { ch.postMessage({ type: 'lock-change', key }) } catch { /* */ }
  }
  // BroadcastChannel only notifies OTHER tabs; StorageEvent similarly
  // doesn't fire for same-tab writes. Local subscribers (in this tab)
  // get the notification by direct fan-out here.
  fanOut(key)
}

// ─── Lock semantics ──────────────────────────────────────────────────────────

/**
 * True if the lock is currently claimed by some live tab — either this
 * one or another. A "dead" lock (past its LOCK_TIMEOUT) reports false
 * so the next caller can take over.
 */
export function isLockActive(key: string): boolean {
  const state = readLock(key)
  if (!state) return false
  return Date.now() - state.heartbeat < LOCK_TIMEOUT_MS
}

/** True if this tab currently holds the lock. */
export function isLockHeldByThisTab(key: string): boolean {
  const state = readLock(key)
  if (!state) return false
  if (state.tabId !== TAB_ID) return false
  return Date.now() - state.heartbeat < LOCK_TIMEOUT_MS
}

/** True if another (live) tab holds the lock. */
export function isLockHeldByOtherTab(key: string): boolean {
  const state = readLock(key)
  if (!state) return false
  if (state.tabId === TAB_ID) return false
  return Date.now() - state.heartbeat < LOCK_TIMEOUT_MS
}

/**
 * Attempt to claim the lock. Returns true on success.
 *
 * Claim succeeds when:
 *   - No lock exists, OR
 *   - Existing lock is dead (heartbeat older than LOCK_TIMEOUT_MS), OR
 *   - We already hold the lock (idempotent re-acquire).
 *
 * **Race window:** localStorage writes are atomic per call but not
 * transactional across check-then-write. Two tabs could theoretically
 * both observe "no lock" and both write their own — last write wins.
 * We mitigate by the double-read pattern below: write, read back, if
 * the read-back doesn't show our tab id, someone else raced us.
 */
export function tryAcquireTabLock(key: string): boolean {
  const now = Date.now()
  const existing = readLock(key)

  if (existing && existing.tabId !== TAB_ID) {
    const alive = now - existing.heartbeat < LOCK_TIMEOUT_MS
    if (alive) {
      log.debug(`acquire ${key}: held by ${existing.tabId.slice(0, 8)}`)
      return false
    }
    // Dead lock — take it over.
  }

  const nextState: LockState =
    existing?.tabId === TAB_ID
      ? { ...existing, heartbeat: now } // re-acquire idempotent: just refresh
      : { tabId: TAB_ID, acquiredAt: now, heartbeat: now }

  writeLock(key, nextState)

  // Read back. If a concurrent write stomped ours, we don't hold the lock.
  const confirm = readLock(key)
  const got = confirm?.tabId === TAB_ID
  if (got) {
    log.debug(`acquired ${key}`)
  } else {
    log.warn(`acquire ${key}: raced and lost`)
  }
  return got
}

/**
 * Refresh the heartbeat. Idempotent — no-op if we don't hold the lock
 * (someone else took over while we were off doing other work; returning
 * false signals the caller to stop whatever work they were doing).
 *
 * Returns true if we still hold the lock after the heartbeat.
 */
export function heartbeatTabLock(key: string): boolean {
  const existing = readLock(key)
  if (!existing || existing.tabId !== TAB_ID) return false

  writeLock(key, { ...existing, heartbeat: Date.now() })
  return true
}

/** Release the lock. Idempotent — no-op if we don't hold it or it doesn't
 * exist. Other tabs' locks are NEVER released by this function; we only
 * clear our own. */
export function releaseTabLock(key: string): void {
  const existing = readLock(key)
  if (!existing) return
  if (existing.tabId !== TAB_ID) {
    log.debug(`release ${key}: held by another tab, no-op`)
    return
  }
  deleteLock(key)
  log.debug(`released ${key}`)
}

// ─── Cross-tab subscription ──────────────────────────────────────────────────

type LockChangeListener = (key: string) => void
const listeners = new Set<LockChangeListener>()

let listenerInstalled = false
function installGlobalListeners(): void {
  if (listenerInstalled) return
  listenerInstalled = true

  const ch = getChannel()
  if (ch) {
    ch.addEventListener('message', (e) => {
      if (e.data?.type === 'lock-change' && typeof e.data.key === 'string') {
        fanOut(e.data.key)
      }
    })
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (!e.key || !e.key.startsWith(STORAGE_PREFIX)) return
      fanOut(e.key.slice(STORAGE_PREFIX.length))
    })
  }
}

function fanOut(key: string): void {
  for (const listener of listeners) {
    try { listener(key) } catch { /* never let a listener break the loop */ }
  }
}

/**
 * Subscribe to lock changes — either on this tab (acquire / release /
 * heartbeat from local code) or from another tab (cross-tab
 * BroadcastChannel / StorageEvent).
 *
 * Listener receives the lock key that changed. Doesn't pass the new
 * state — listeners should call `isLockHeldByThisTab(key)` etc. to
 * make their own decision.
 */
export function subscribeTabLock(listener: LockChangeListener): () => void {
  installGlobalListeners()
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

// ─── Test helpers ────────────────────────────────────────────────────────────

/** Test-only reset. Clears local state + notifies subscribers. */
export function _resetTabLocks(): void {
  try {
    const toDelete: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(STORAGE_PREFIX)) toDelete.push(k)
    }
    for (const k of toDelete) localStorage.removeItem(k)
  } catch { /* */ }
}
